<template>
  <div>
    <slot name="header" :documents="documents"></slot>
    <div v-if="$online">
      <v-list class="bg-main py-0" flat>
        <div class="transparent mx-1 mb-1" v-if="showFilters">
          <v-row class="px-4 pb-6" align="center">
            <v-col cols="12" md="6" class="pb-0">
              <v-text-field
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('t_search')"
                v-model="documentsSearch"
                @input="searchDocuments"
              ></v-text-field>
            </v-col>
          </v-row>
          <div class="px-4 pt-0" v-if="tagsList && tagsList.length">
            <div class="d-flex align-center" style="gap: 8px; overflow-x: auto;">
              <tag-chips :tags="tagsList" :isSelectable="true" v-model="documentsTags" />
              <v-btn
                v-if="documentsTags && documentsTags.length"
                small
                text
                color="#1976d2"
                @click="documentsTags = []"
                class="text-bold text-caption flex-shrink-0 ml-auto"
                style="height: 24px; min-width: auto; padding: 0 8px"
              >
                <v-icon small class="mr-1">mdi-close</v-icon>
                {{ $t("t_clear") }}
              </v-btn>
            </div>
          </div>
        </div>
        <div>
          <div v-for="(item, index) in documents" :key="item.header">
            <v-subheader v-if="item.header" v-text="item.header"></v-subheader>
            <v-divider v-else-if="item.divider" :key="index" :inset="item.inset"></v-divider>
            <div v-else-if="item.isPaperOs">
              <v-list-item
                class="document-item paperos-item rounded mx-2 pr-3 mb-1"
                :class="{ 'grey darken-4': $vuetify.theme.dark }"
                :key="item.id"
              >
                <v-list-item-content>
                  <v-list-item-subtitle v-if="item" class="text-caption">
                    <span v-if="item.recipients[0].signed" class="green--text text--lighten-1">
                      {{ $t("t_completed") }}
                    </span>
                    <span v-else class="red--text text--darken-2"> {{ $t("t_pending_signature") }} </span>
                  </v-list-item-subtitle>
                  <v-list-item-title class="text-wrap">{{ item.fileName }}</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-btn
                      small
                      outlined
                      v-if="item.recipients[0].signed"
                      class="mt-2"
                      :href="item.recipients[0].signatureUri"
                      target="_blank"
                    >
                      <v-icon small class="mr-2">mdi-open-in-new</v-icon> {{ $t("t_view_document") }}
                    </v-btn>
                    <v-btn
                      color="primary"
                      small
                      v-if="!item.recipients[0].signed"
                      class="mt-2"
                      :href="item.recipients[0].signatureUri"
                      target="_blank"
                    >
                      <v-icon small class="mr-2">mdi-open-in-new</v-icon> {{ $t("t_view_and_sign") }}
                    </v-btn>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </div>
            <DocumentListItem
              v-else
              ref="documentListItem"
              :item="item"
              :parent="'documents'"
              :tagsList="tagsList"
              :key="'document-' + index"
              @showDocument="showDocument(item)"
            />
          </div>
        </div>
      </v-list>
      <div v-if="showLoadMore" class="d-flex justify-center my-2">
        <v-progress-circular indeterminate color="primary" size="32" />
      </div>

      <div v-show="hasMore && !preview" ref="infiniteScrollTrigger" style="height: 1px; margin-top: 20px"></div>
    </div>
    <h4 class="pa-4 pt-0" v-if="(documents?.length == 0 && !loading) || !$online">
      {{ $t("c_documentlist.t_no_documents") }} {{ !$online ? " : " + $t("t_no_network_connection") : "" }}
    </h4>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import Helpers from "@/mixins/helpers";
import DocumentListItem from "@/components/documents/DocumentListItem.vue";
import debounce from "lodash/debounce";
import { DocumentService, TagService, UserService } from "@/services";
import { App } from "@capacitor/app";
import { TagChips } from "@ranes-inc/shared-lib";

export default {
  name: "DocumentList",
  mixins: [Helpers],
  components: { DocumentListItem, TagChips },
  props: {
    documentsTakeNum: {
      type: Number,
      default: 20,
    },
    showFilters: {
      type: Boolean,
      default: true,
    },
    preview: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    documents: [],
    documentsOrderByField: "Id",
    documentsAscending: false,
    documentsSkipNum: 0,
    documentsSearch: "",
    documentsTags: [],
    showLoadMore: false,
    appStateListenerAdded: false,
    tagsList: [],
    hasMore: true,
    observer: null,
  }),
  methods: {
    ...mapActions("document", ["getDocuments"]),
    async loadDocuments(refresh = false) {
      if (!this.$online) return;

      if (refresh) {
        this.documents = [];
        this.hasMore = true;
        this.documentsSkipNum = 0;
      }

      if (this.loading || this.showLoadMore) return;

      if (this.documentsSkipNum === 0) {
        this.loading = true;
      } else {
        this.showLoadMore = true;
      }

      this.showLoadBar();

      const params = {
        "paging.orderbyfield": this.documentsOrderByField,
        "paging.ascending": this.documentsAscending,
        "paging.skipnum": this.documentsSkipNum,
        "paging.takenum": this.documentsTakeNum,
      };
      if (this.documentsSearch) {
        params.content = this.documentsSearch;
      }
      if (Array.isArray(this.documentsTags) && this.documentsTags.length > 0) {
        params.tag = this.documentsTags.map((tag) => tag.id).join(",");
      }
      try {
        let legalDocuments = await this.loadDocumentsLegal();
        if (this.documentsSearch && legalDocuments) {
          legalDocuments = legalDocuments.filter((docs) =>
            docs.fileName.toLowerCase().includes(this.documentsSearch.toLowerCase()),
          );
        }
        const r = await this.getDocuments(params);

        const documents = r.data?.map((doc) => ({ ...doc, isRanes: true })) || [];
        const signedDocs = legalDocuments?.filter((docs) => docs.recipients?.[0]?.signed) || [];
        const unSignedDocs = legalDocuments?.filter((docs) => docs.recipients?.[0] && !docs.recipients[0].signed) || [];

        const allDocuments = [...unSignedDocs, ...documents, ...signedDocs];
        allDocuments.forEach((m) => {
          if (!this.documents.some((doc) => doc.id === m.id)) {
            this.documents.push(m);
          }
        });
        this.documentsSkipNum = this.documents.length;
        if (r.meta?.endOfResults) {
          this.hasMore = false;
        } else {
          this.hasMore = true;
        }
      } finally {
        this.loading = false;
        this.showLoadMore = false;
        this.hideLoadBar();
        //Show or hide documents if they exist or not - nav defined on app data
        if (this.documents.length == 0) {
          this.$root.nav = this.$root.nav.filter((item) => item !== "documents");
        } else {
          !this.$root.nav.includes("documents") ? this.$root.nav.push("documents") : null;
        }
      }
    },

    loadMoreDocuments() {
      this.documentsSkipNum = this.documents.length;
      this.loadDocuments();
    },

    async loadMoreSubmissions() {
      this.submissionsSkipNum = this.submissions.length;
      this.loadSubmissions();
    },

    searchDocuments: debounce(function () {
      this.loadDocuments(true);
    }, 500),

    async loadDocumentsLegal() {
      const r = await UserService.getUser();
      const id = r.data?.paperOSUserIntegrationId;
      if (!id) {
        return;
      }
      const ri = await DocumentService.getDocumentUserIntegration(id);
      const integration = ri?.data;
      const meta = JSON.parse(ri.data.metaData);
      const d = await DocumentService.getDocumentsPaperOs({ orgId: meta.org_id, email: integration.user?.email });
      let docs = d.data?.documents;
      docs = docs.map((doc) => ({ ...doc, isPaperOs: true }));
      return docs;
    },

    appStateChangeHandler(state) {
      if (state.isActive) {
        this.loadDocuments(true);
      }
    },
    async loadTags() {
      try {
        const response = await TagService.searchTags({
          taggableEntity: ["document"],
          paging: {
            takeNum: 100,
          },
        });
        this.tagsList = response.data;
      } catch (error) {
        console.error("Error loading tags:", error);
      }
    },
    // Initialize documents and tags data.
    initData() {
      this.loadDocuments();
      this.loadTags();
    },
    //  If preview mode is enabled, show loading indicator.
    handlePreview() {
      if (this.preview) {
        this.loading = true;
      }
    },
    /**
     * Register the app state change listener if not already added.
     * Prevents duplicate listeners.
     */
    registerAppStateListener() {
      if (!this.appStateListenerAdded) {
        App.addListener("appStateChange", this.appStateChangeHandler);
        this.appStateListenerAdded = true;
      }
    },

    /**
     * Setup IntersectionObserver for infinite scroll.
     */
    initInfiniteScroll() {
      // Don't initialize infinite scroll in preview (on dashboard) mode
      if (this.preview) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && this.hasMore && !this.showLoadMore) {
            this.loadDocuments();
          }
        },
        { threshold: 1.0 },
      );

      if (this.$refs.infiniteScrollTrigger) {
        this.observer.observe(this.$refs.infiniteScrollTrigger);
      }
    },
  },
  watch: {
    documentsTags() {
      this.loadDocuments(true);
    },
  },
  mounted() {
    this.initData();
    this.handlePreview();
    this.registerAppStateListener();
    this.initInfiniteScroll();
  },
  beforeDestroy() {
    if (this.appStateListenerAdded) {
      App.removeAllListeners();
      this.appStateListenerAdded = false;
    }

    // clean up observers
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>

<style lang="scss">
@import "@/scss/variables";

.v-application .documents .paperos-item {
  border-left: 4px solid $color-documents !important;
}
</style>
